import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import { Form } from 'react-bootstrap'
import { useSearchParams } from 'react-router-dom'
import GlobalStyles from '../component-items/styles'
import { SearchBox } from '../component-items/search'
import { TableCell } from '../component-items/datatable'
import CardLoading from '../component-items/loading-popover'
import { useFetch } from '../component-items/helpers'

// Fontawesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faTimes } from '@fortawesome/pro-duotone-svg-icons'

type MatrixLevel = 'admin' | 'operator' | 'viewer' | 'na' | 'none' | string

interface MatrixRow {
  segment: string
  feature: string
  level: MatrixLevel
  warehouse: boolean
  merchant: boolean
}

const SEGMENT_LABELS: Record<string, string> = {
  outbound: 'Outbound',
  inbound: 'Inbound',
  inventory: 'Inventory',
  products: 'Products',
  vas: 'VAS',
  billing: 'Billing',
  postage: 'Postage',
  integrations: 'Integrations',
  settings: 'Settings',
  reports: 'Reports',
}

const FILTER_CONTROL_WIDTH = 220
const FILTER_CONTROL_HEIGHT = 31

const buildDisplayRows = (inputRows: MatrixRow[]) => {
  return inputRows.map((row) => ({
    segment: String(row.segment || '')
      .toLowerCase()
      .trim(),
    feature: String(row.feature || '').trim(),
    level: (String(row.level || 'none').toLowerCase() as MatrixLevel) || 'none',
    warehouse: Boolean(row.warehouse),
    merchant: Boolean(row.merchant),
  }))
}

const SEGMENT_OPTIONS = [
  { value: '', label: 'All Segments' },
  { value: 'outbound', label: 'Outbound' },
  { value: 'inbound', label: 'Inbound' },
  { value: 'inventory', label: 'Inventory' },
  { value: 'products', label: 'Products' },
  { value: 'vas', label: 'VAS' },
  { value: 'billing', label: 'Billing' },
  { value: 'postage', label: 'Postage' },
  { value: 'integrations', label: 'Integrations' },
  { value: 'settings', label: 'Settings' },
  { value: 'reports', label: 'Reports' },
]

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 1rem;
  margin-top: 1rem;
  align-items: flex-end;
  background: #fff;
  border: 1px solid #ebedf2;
  border-radius: 4px;
`

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 170px;

  label {
    font-size: 0.8em;
    font-weight: 600;
    color: #6c757d;
  }

  .form-control,
  .form-select,
  select {
    height: ${FILTER_CONTROL_HEIGHT}px !important;
    min-height: ${FILTER_CONTROL_HEIGHT}px !important;
    padding-top: 0.2rem !important;
    padding-bottom: 0.2rem !important;
  }
`

const ClearFiltersBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: ${FILTER_CONTROL_HEIGHT}px;
  padding: 0 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  background: #fff;
  color: #495057;
  font-size: 0.85rem;
  cursor: pointer;

  &:hover {
    background: #f1f3f5;
  }
`

const TableContainer = styled.div`
  max-height: 650px;
  overflow: auto;
  margin-top: 1rem;
  border: 1px solid #ebedf2;
  border-radius: 8px;
`

const StyledTable = styled(GlobalStyles.DataTable)`
  margin-bottom: 0;

  th,
  td {
    border: 1px solid #ebedf2;
  }

  thead th {
    background: #f8f9fa;

    span.text {
      color: #48465b;
      font-weight: 600;
      font-size: 0.85em;
      letter-spacing: 0;
    }
  }

  tbody td:first-child {
    background-color: #f8f9fa;
  }

  tbody tr:hover {
    background-color: rgba(0, 0, 0, 0.02);
  }

  tbody tr:hover td:first-child {
    background-color: #f0f1f3;
  }
`

const SegmentBadge = styled.span<{ $segment: string }>`
  display: inline-block;
  padding: 0.3em 0.6em;
  border-radius: 4px;
  font-weight: 500;
  font-size: 0.8em;
  min-width: 70px;
  text-align: center;
  background-color: ${({ $segment }) => {
    switch ($segment) {
      case 'outbound':
        return '#e8f5e9'
      case 'inbound':
        return '#e3f2fd'
      case 'inventory':
        return '#fff3e0'
      case 'products':
        return '#f3e5f5'
      case 'vas':
        return '#e0f7fa'
      case 'billing':
        return '#fce4ec'
      case 'postage':
        return '#f1f8e9'
      case 'integrations':
        return '#ede7f6'
      case 'settings':
        return '#e8eaf6'
      case 'reports':
        return '#eceff1'
      default:
        return '#f5f5f5'
    }
  }};
  color: ${({ $segment }) => {
    switch ($segment) {
      case 'outbound':
        return '#2e7d32'
      case 'inbound':
        return '#1565c0'
      case 'inventory':
        return '#e65100'
      case 'products':
        return '#7b1fa2'
      case 'vas':
        return '#006064'
      case 'billing':
        return '#ad1457'
      case 'postage':
        return '#558b2f'
      case 'integrations':
        return '#5e35b1'
      case 'settings':
        return '#3949ab'
      case 'reports':
        return '#546e7a'
      default:
        return '#9e9e9e'
    }
  }};
`

const FeatureText = styled.div`
  color: #212529;
  font-weight: 500;
  font-size: 0.8em;
`

const CheckWrapper = styled.div`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 22px;
  height: 22px;
  border-radius: 999px;
  background: #e8f7ee;
  color: #198754;
`

const XWrapper = styled.div`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 22px;
  height: 22px;
  border-radius: 999px;
  background: #fdecec;
  color: #dc3545;
`

const LevelBadge = styled.span<{ $level: string }>`
  display: inline-block;
  padding: 0.3em 0.6em;
  border-radius: 4px;
  font-weight: 500;
  font-size: 0.8em;
  min-width: 55px;
  text-align: center;
  background-color: ${({ $level }) => {
    switch ($level) {
      case 'admin':
        return '#f3e5f5'
      case 'operator':
        return '#e8f5e9'
      case 'viewer':
        return '#e3f2fd'
      case 'none':
      default:
        return '#f5f5f5'
    }
  }};
  color: ${({ $level }) => {
    switch ($level) {
      case 'admin':
        return '#7b1fa2'
      case 'operator':
        return '#2e7d32'
      case 'viewer':
        return '#1565c0'
      case 'none':
      default:
        return '#9e9e9e'
    }
  }};
`

const EmptyState = styled.div`
  padding: 2.5rem 1rem;
  text-align: center;
  color: #6b7280;
`

export const PermissionMatrixTab = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const initialSegmentFromUrl = (searchParams.get('matrixSegment') || '').toLowerCase()
  const isValidSegment = SEGMENT_OPTIONS.some((opt) => opt.value === initialSegmentFromUrl)

  const [search, setSearch] = useState('')
  const [segmentFilter, setSegmentFilter] = useState(isValidSegment ? initialSegmentFromUrl : '')

  const { response: matrixResponse, loaded, error, placeholder }: any = useFetch('/api/rbac/matrix/', {})
  const rawRows = useMemo(() => {
    if (Array.isArray(matrixResponse)) {
      return matrixResponse as MatrixRow[]
    }

    if (Array.isArray(matrixResponse?.data)) {
      return matrixResponse.data as MatrixRow[]
    }

    return []
  }, [matrixResponse])

  const rows = useMemo(() => buildDisplayRows(rawRows), [rawRows])
  const hasFilters = Boolean(segmentFilter || search.trim())

  const handleSegmentFilterChange = (nextSegment: string) => {
    setSegmentFilter(nextSegment)

    const nextParams = new URLSearchParams(searchParams)
    if (nextSegment) {
      nextParams.set('matrixSegment', nextSegment)
    } else {
      nextParams.delete('matrixSegment')
    }

    setSearchParams(nextParams)
  }

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase()

    return rows.filter((row) => {
      const segmentKey = String(row.segment || '')
        .toLowerCase()
        .trim()
      const matchesSegment = !segmentFilter || segmentKey === segmentFilter

      if (!query) {
        return matchesSegment
      }

      const matchesSearch =
        segmentKey.includes(query) ||
        row.feature.toLowerCase().includes(query) ||
        String(row.level).toLowerCase().includes(query)

      return matchesSearch && matchesSegment
    })
  }, [rows, search, segmentFilter])

  if (!loaded) {
    return <CardLoading text={placeholder} error={error} />
  }

  return (
    <div style={{ minHeight: 500, padding: '0 1em' }}>
      <GlobalStyles.CardHeader style={{ padding: 0 }}>
        <GlobalStyles.CardTitle>
          <h4 style={{ margin: 0, color: '#1f2937', fontWeight: 700 }}>
            Permission Matrix<small style={{ marginLeft: '0.7rem' }}>{filteredRows.length} Total</small>
          </h4>
        </GlobalStyles.CardTitle>
      </GlobalStyles.CardHeader>

      <FilterBar>
        <FilterGroup>
          <label>Segment</label>
          <Form.Select
            style={{ width: FILTER_CONTROL_WIDTH }}
            value={segmentFilter}
            onChange={(e) => handleSegmentFilterChange(e.target.value)}
          >
            {SEGMENT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Form.Select>
        </FilterGroup>

        <FilterGroup>
          <label>Search</label>
          <SearchBox
            {...{ search, setSearch }}
            page={1}
            setPage={() => undefined}
            loading={false}
            setLoading={() => undefined}
            setFocus
            updateURL={false}
            style={{ width: FILTER_CONTROL_WIDTH }}
          />
        </FilterGroup>

        {hasFilters && (
          <ClearFiltersBtn
            onClick={() => {
              handleSegmentFilterChange('')
              setSearch('')
            }}
          >
            <FontAwesomeIcon icon={faTimes} />
            Clear Filters
          </ClearFiltersBtn>
        )}
      </FilterBar>

      <TableContainer>
        <StyledTable>
          <thead>
            <tr>
              <th>
                <span className="text">Segment</span>
              </th>
              <th>
                <span className="text">Feature</span>
              </th>
              <th>
                <span className="text center">Level</span>
              </th>
              <th>
                <span className="text center">Warehouse</span>
              </th>
              <th>
                <span className="text center">Merchant</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.length > 0 ? (
              filteredRows.map((row, idx) => {
                const segmentKey = String(row.segment || '').toLowerCase()
                return (
                  <GlobalStyles.TableRow key={`${row.segment}-${row.feature}-${idx}`}>
                    <TableCell
                      input={
                        <SegmentBadge $segment={segmentKey}>{SEGMENT_LABELS[segmentKey] || row.segment}</SegmentBadge>
                      }
                    />
                    <TableCell input={<FeatureText>{row.feature}</FeatureText>} />
                    <TableCell center input={<LevelBadge $level={String(row.level)}>{row.level}</LevelBadge>} />
                    <TableCell
                      center
                      input={
                        row.warehouse ? (
                          <CheckWrapper>
                            <FontAwesomeIcon icon={faCheck} />
                          </CheckWrapper>
                        ) : (
                          <XWrapper>
                            <FontAwesomeIcon icon={faTimes} />
                          </XWrapper>
                        )
                      }
                    />
                    <TableCell
                      center
                      input={
                        row.merchant ? (
                          <CheckWrapper>
                            <FontAwesomeIcon icon={faCheck} />
                          </CheckWrapper>
                        ) : (
                          <XWrapper>
                            <FontAwesomeIcon icon={faTimes} />
                          </XWrapper>
                        )
                      }
                    />
                  </GlobalStyles.TableRow>
                )
              })
            ) : (
              <tr>
                <td colSpan={5}>
                  <EmptyState>No matrix rows match your search.</EmptyState>
                </td>
              </tr>
            )}
          </tbody>
        </StyledTable>
      </TableContainer>
    </div>
  )
}
